import { useMemo } from "react";

import { Box, Stack } from "metabase/ui";
import { color } from "metabase/ui/colors";
import {
  type StackedTooltipModel,
  getPercent,
  getSortedRows,
  getTotalValue,
  groupExcessiveTooltipRows,
} from "metabase/viz-core";

import { TooltipRow, TooltipTotalRow } from "../TooltipRow";

import S from "./StackedDataTooltip.module.css";

const MAX_BODY_ROWS = 8;

type StackedDataTooltipProps = StackedTooltipModel;

const StackedDataTooltip = ({
  headerTitle,
  headerRows,
  bodyRows = [],
  grandTotal,
  showTotal,
  showPercentages,
  totalFormatter = (value: unknown) => String(value),
}: StackedDataTooltipProps) => {
  const sortedHeaderRows = useMemo(
    () => getSortedRows(headerRows),
    [headerRows],
  );
  const sortedBodyRows = useMemo(() => getSortedRows(bodyRows), [bodyRows]);
  const rowsTotal = useMemo(
    () => getTotalValue(sortedHeaderRows, sortedBodyRows),
    [sortedHeaderRows, sortedBodyRows],
  );

  const isShowingTotalSensible =
    sortedHeaderRows.length + sortedBodyRows.length > 1;
  const hasColorIndicators = useMemo(
    () =>
      [...sortedBodyRows, ...sortedHeaderRows].some((row) => row.color != null),
    [sortedHeaderRows, sortedBodyRows],
  );

  // For some charts such as PieChart we intentionally show only certain data rows that do not represent the full data.
  // In order to calculate percentages correctly we provide the grand total value
  const percentCalculationTotal = grandTotal ?? rowsTotal;

  const trimmedBodyRows = groupExcessiveTooltipRows(
    sortedBodyRows,
    MAX_BODY_ROWS,
    hasColorIndicators ? color("text-disabled") : undefined,
  );

  return (
    <Stack gap={0} pt="lg">
      {headerTitle && (
        <Box
          component="header"
          tt="uppercase"
          fz="sm"
          px="xl"
          pb="xxs"
          data-testid="tooltip-header"
        >
          {headerTitle}
        </Box>
      )}
      <table className={S.dataPointTable}>
        <thead
          className={sortedBodyRows.length > 0 ? S.tableRowSpacing : undefined}
        >
          {sortedHeaderRows.map((row, index) => (
            <TooltipRow
              key={index}
              isHeader
              percent={
                showPercentages ? getPercent(rowsTotal, row.value) : undefined
              }
              {...row}
            />
          ))}
        </thead>

        {trimmedBodyRows.length > 0 && (
          <tbody className={S.dataPointTableBody}>
            {trimmedBodyRows.map((row, index) => (
              <TooltipRow
                key={index}
                percent={
                  showPercentages ? getPercent(rowsTotal, row.value) : undefined
                }
                {...row}
              />
            ))}
          </tbody>
        )}

        {showTotal && isShowingTotalSensible && (
          <tfoot className={S.dataPointTableFooter}>
            <TooltipTotalRow
              value={totalFormatter(rowsTotal)}
              hasIcon={hasColorIndicators}
              percent={
                showPercentages
                  ? getPercent(percentCalculationTotal, rowsTotal)
                  : undefined
              }
            />
          </tfoot>
        )}
      </table>
    </Stack>
  );
};

// eslint-disable-next-line import/no-default-export -- deprecated usage
export default StackedDataTooltip;
